import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { ModalShell } from './ui/ModalShell'

export interface ConfirmOptions {
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  variant?: 'default' | 'danger'
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null)

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null)
  const resolverRef = useRef<((value: boolean) => void) | null>(null)

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve
      setOpts(options)
    })
  }, [])

  function close(value: boolean) {
    resolverRef.current?.(value)
    resolverRef.current = null
    setOpts(null)
  }

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {opts && (
        <ConfirmDialog
          options={opts}
          onConfirm={() => close(true)}
          onCancel={() => close(false)}
        />
      )}
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext)
  if (!ctx) {
    throw new Error('useConfirm() debe usarse dentro de <ConfirmProvider>')
  }
  return ctx.confirm
}

interface ConfirmDialogProps {
  options: ConfirmOptions
  onConfirm: () => void
  onCancel: () => void
}

function ConfirmDialog({ options, onConfirm, onCancel }: ConfirmDialogProps) {
  const {
    title,
    message,
    confirmText = 'Confirmar',
    cancelText = 'Cancelar',
    variant = 'default',
  } = options

  const confirmClass =
    variant === 'danger'
      ? 'bg-avante-danger text-white'
      : 'bg-avante-primary text-white'

  return (
    <ModalShell title={title} onClose={onCancel} maxWidth={440} zIndex={60}>
      <p className="text-sm text-gray-700 mb-5 whitespace-pre-line">{message}</p>
      <div className="flex gap-2">
        <button
          onClick={onCancel}
          className="flex-1 border border-gray-300 py-2 rounded text-sm"
        >
          {cancelText}
        </button>
        <button
          onClick={onConfirm}
          className={`flex-1 py-2 rounded text-sm font-semibold ${confirmClass}`}
          autoFocus
        >
          {confirmText}
        </button>
      </div>
    </ModalShell>
  )
}
